import { DbRef, ItineraryEditorStatus } from '@caresend/types';
import { objectMap, asyncTimeout, SECOND, isNullish, diffObjectsDeep, isEncrypted, objectFilter, isObject } from '@caresend/utils';
import { getDatabase, ref, push } from 'firebase/database';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { defineStore } from 'pinia';
import { cleanAndUpdate } from '../../portal/helpers/helpers.js';
import { injectNullsDeep } from '../../portal/utils/injectNulls.js';
import { maybeGetEncryptedValue } from './encryption.js';
import { unobstructed } from './unobstructed.js';
import { setNestedChildOnRecord } from '../recordManager/helper.js';
import { autoState } from '../state/state.js';
import { decryptData } from '../../../../../../database/encryption/decrypt.js';
import 'firebase/auth';
import '../../../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../../../database/firebaseConfig/init.js';
import { getValueOnce } from '../../../../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../../ui/Icon.vue.js';
import '../../../../../../functions/configuration/icons.js';
import '../../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../../functions/tracking/index.js';
import '../../../../../../router/helpers/initRouter.js';
import { getStore } from '../../../../../../store/helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';

/** Constants driving database synchronization */
/**
 * TODO: DbSyncStore currently persists unfinished updates. Though, these
 * writes are not honored.
 */
/** Maximum time to wait for the main thread to free up before synchronizing */
const maxElapsedTime = 7 * SECOND;
/** Minimum time to wait for the main thread to free up before synchronizing */
const minElapsedTime = SECOND;
/** Time to wait before purging last diffs */
const diffPurgeTime = (minElapsedTime + maxElapsedTime) * 2;
const getUpdatesWithHistory = async (updates) => {
    const promises = Object.entries(updates).map(async ([path, val]) => {
        const { description, value: newValue } = val;
        const maybeEncryptedPrevValue = await getValueOnce(path);
        const valueIsEncrypted = isEncrypted(maybeEncryptedPrevValue);
        try {
            const unencryptedPrevValue = valueIsEncrypted
                ? await decryptData(maybeEncryptedPrevValue)
                : maybeEncryptedPrevValue;
            const shouldUpdate = !isEqual(unencryptedPrevValue, newValue);
            if (shouldUpdate) {
                const maybeEncryptedNextValue = await maybeGetEncryptedValue(path, newValue);
                return [path, {
                        description,
                        prev: unencryptedPrevValue,
                        next: maybeEncryptedNextValue,
                        shouldUpdate,
                    }];
            }
        }
        catch (error) {
            console.error('Error getting updates with history at path: ', path, error);
        }
        return [path, { shouldUpdate: false }];
    });
    const updatesWithHistory = await Promise.all(promises);
    return Object.fromEntries(updatesWithHistory);
};
/**
 * Store for tracking and synchronizing database writes
 *
 * This could be decoupled from Vue/Pinia entirely, but this coupling
 * allows for monitoring the state of writes in a UI component.
 * Should discuss with further evalutation.
 */
const useDbSyncStore = defineStore('dbSync', () => {
    const store = getStore();
    const state = autoState({
        editHistoryPaths: {},
        loading: false,
        nextUpdates: {},
        writeBacklog: {},
        latestUpdateContext: null,
    }, {
        persist: {
            lsKey: 'dbSyncStore',
        },
    });
    const diffs = {};
    /**
     * On every database write, track diffs of the previous and next values.
     * Purges after 5 seconds
     */
    const trackDiffs = async (context, path, diff) => {
        var _a;
        diffs[context] = {
            ...diffs[context],
            [path]: diff,
        };
        await asyncTimeout(diffPurgeTime);
        (_a = diffs[context]) === null || _a === void 0 ? true : delete _a[path];
    };
    /**
     * On every database response, if the incoming value is the same as the previous value
     * that was written within 5 seconds, then the database response might be innacurate.
     */
    const validateIncomingDiffs = (context, path, _incomingDiffs, keysToInvalidate = []) => {
        const invalidDiffs = {};
        const incomingDiffs = objectMap(_incomingDiffs, (v) => v, (subpath) => `${path}/${subpath}`);
        Object.entries(incomingDiffs).forEach(([subpath, diff]) => {
            var _a;
            if (keysToInvalidate.find((key) => subpath.includes(key.toString()))) {
                console.info('❌ Not a useful diff: ', subpath);
                invalidDiffs[subpath] = diff.prev;
                return;
            }
            const tracked = cloneDeep((_a = diffs[context]) === null || _a === void 0 ? void 0 : _a[subpath]);
            if (diff.next === (tracked === null || tracked === void 0 ? void 0 : tracked.prev)) {
                const localPath = subpath.replace(`${path}/`, '');
                invalidDiffs[localPath] = tracked === null || tracked === void 0 ? void 0 : tracked.next;
                console.info('❌ Invalid diff: ', localPath);
                console.info('Incoming diff: ', diff.next);
                console.info('Replacing with: ', tracked === null || tracked === void 0 ? void 0 : tracked.next);
            }
        });
        return invalidDiffs;
    };
    /**
     * Tie the context of an expected series of db writes to a
     * specific path in edit history
     */
    const provideContext = (context, id) => {
        state.set.editHistoryPaths({
            ...state.value.editHistoryPaths,
            [context]: `${DbRef.HISTORY}/${context}/${id}`,
        });
        state.set.latestUpdateContext(context);
    };
    const resetNextUpdates = (context) => {
        if (context) {
            state.set.nextUpdates({
                ...state.value.nextUpdates,
                [context]: {},
            });
        }
    };
    const resetBacklogByUpdatedPaths = (context, updatedPaths) => {
        var _a;
        const newContextBacklog = objectFilter((_a = state.value.writeBacklog[context]) !== null && _a !== void 0 ? _a : {}, (_, path) => !!path && updatedPaths.has(path));
        state.set.writeBacklog({
            ...state.value.writeBacklog,
            [context]: newContextBacklog,
        });
    };
    const allocateWriteBacklog = (context, updates) => {
        state.set.writeBacklog({
            ...state.value.writeBacklog,
            [context]: {
                ...state.value.writeBacklog[context],
                ...updates,
            },
        });
    };
    const syncUpdates = async (context) => {
        var _a, _b;
        const updatesWithMeta = cloneDeep(state.value.nextUpdates[context]);
        if (!updatesWithMeta || !Object.keys(updatesWithMeta).length)
            return;
        const historyPath = state.value.editHistoryPaths[context];
        if (!historyPath)
            return;
        allocateWriteBacklog(context, updatesWithMeta);
        resetNextUpdates(context);
        state.set.loading(true);
        const editTimestamp = Date.now();
        /** TODO: Rename @caresend/types val to `EditHistory` */
        const historyUpdates = {};
        const nextUpdates = cloneDeep(updatesWithMeta);
        let allUpdates = [];
        const updatesWithHistory = await getUpdatesWithHistory(nextUpdates);
        Object.entries(updatesWithHistory).forEach(([path, val]) => {
            if (isNullish(path) || !val.shouldUpdate)
                return;
            /**
             * TODO: Once diffObjectsDeep is analyzed comprehensively, we can
             * use the length of this array to discard the update as handled just above.
             */
            const objDiffs = diffObjectsDeep(val.prev, val.next);
            const explicitDeletions = Object.entries(objDiffs).filter(
            /**
             * These explicitly defined deleted nodes and any subpaths defined as an
             * array index value are not considered deletions.
             */
            ([p, v]) => {
                const isNullishValue = isNullish(v.next);
                const isSubpath = !p.match(/^(?!.*\/\d+\/).*$/);
                const isDeletion = isNullishValue && isSubpath;
                return isDeletion;
            });
            const nextUpdateValue = val.next;
            if (explicitDeletions.length) {
                explicitDeletions.forEach(([subpath]) => {
                    if (subpath.includes('/')) {
                        setNestedChildOnRecord(nextUpdateValue, subpath, null);
                    }
                    else {
                        nextUpdateValue[subpath] = null;
                    }
                });
            }
            const getShouldDelete = (value) => {
                if (isNullish(value))
                    return true;
                if (Array.isArray(value))
                    return !value.length;
                if (isObject(value))
                    return !Object.keys(value).length;
                return false;
            };
            historyUpdates[encodeURIComponent(path)] = val;
            allUpdates.push([
                path,
                getShouldDelete(nextUpdateValue) ? null : nextUpdateValue,
            ]);
        });
        const userID = (_b = (_a = store.state.auth.user) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '';
        const newEditHistory = {
            editBy: userID,
            editTimestamp,
            updates: historyUpdates,
        };
        allUpdates = [
            ...allUpdates,
            [`${historyPath}/creationHistory/editingUsers/${userID}`, {
                    status: ItineraryEditorStatus.ONLINE,
                    timestamp: Date.now(),
                }],
        ];
        await cleanAndUpdate(allUpdates);
        const cleanEditHistory = injectNullsDeep(newEditHistory);
        if (cleanEditHistory === null || cleanEditHistory === void 0 ? void 0 : cleanEditHistory.length) {
            console.info('Writing edit history...', cleanEditHistory.updates);
        }
        if (!Object.values(cleanEditHistory !== null && cleanEditHistory !== void 0 ? cleanEditHistory : {}).length)
            return;
        /**
         * TODO: Use `generateSortableID` to put the new value at the first position of the list.
         * Edit history component will need to be updated to support this
         */
        const db = getDatabase();
        const dbRef = ref(db, `${historyPath}/creationHistory/editHistory`);
        push(dbRef, cleanEditHistory)
            .catch((error) => console.error('Error updating edit history: ', error))
            .finally(() => {
            resetBacklogByUpdatedPaths(context, new Set(Object.keys(updatesWithMeta)));
            state.set.loading(false);
        });
    };
    const threadObstructions = {};
    const timeouts = {};
    const awaitSubsequentUpdates = (context) => {
        if (threadObstructions[context] || timeouts[context]) {
            clearInterval(threadObstructions[context]);
            clearTimeout(timeouts[context]);
        }
        const requestTime = performance.now();
        const release = () => {
            clearInterval(threadObstructions[context]);
            clearTimeout(timeouts[context]);
            const syncTime = performance.now();
            const elapsedSeconds = (syncTime - requestTime) / 1000;
            console.info('Main thread free after', `${elapsedSeconds.toFixed(2)} seconds`);
            syncUpdates(context);
        };
        timeouts[context] = setTimeout(() => {
            threadObstructions[context] = unobstructed(release, {
                threshold: 10,
                maxTime: maxElapsedTime,
            });
        }, minElapsedTime);
    };
    const queueNextUpdate = (path, description, value, context, updateDiffs = {}) => {
        var _a;
        const updateContext = (_a = state.value.latestUpdateContext) !== null && _a !== void 0 ? _a : context;
        state.set.nextUpdates({
            ...state.value.nextUpdates,
            [updateContext]: {
                ...state.value.nextUpdates[updateContext],
                [path]: {
                    description,
                    value,
                },
            },
        });
        objectMap(updateDiffs, (diff, subpath) => trackDiffs(context, subpath, diff));
        awaitSubsequentUpdates(updateContext);
    };
    return {
        provideContext,
        queueNextUpdate,
        validateIncomingDiffs,
        state,
    };
});

export { useDbSyncStore };
